import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { useDispatch } from "react-redux";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";

import classes from "./AuthComponent.module.css";
import { AuthContext } from "../../context/auth-context";
import { doLogin, resetPassword } from "../../DatabaseFirebase/firebaseService";
import ForgotPasswordModal from "./ForgotPasswordModal";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import { fetchCustomer } from "../../redux/actions/userActions";
import { fetchLLMOptions } from "../../redux/actions/llmOptionsActions";
import MUIButton from "../UI/MUIButton/MUIButton";
import loginBg from "./../../assets/images/login-bg.svg";
import brandIcon from "./../../assets/images/brand-icon.svg";
import { newVersionAvailableToRefresh } from "../../services/apiService";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const AuthComponent = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isClicked, setIsClicked] = useState(false);
  const [isShowModal, setIsShowModal] = useState(false);
  const [emailToResetPass, setEmailToResetPass] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setUser, clearSession } = useContext(AuthContext);

  const handleClose = () => {
    setIsShowModal(false);
    setSuccessMessage("");
    setEmailToResetPass("");
  };

  const handleResetPassword = async () => {
    try {
      await resetPassword(emailToResetPass);
      toast.success("Password reset email sent. Please check your inbox.");
    } catch (error) {
      console.log(error.message);
      toast.error("Something went wrong");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsClicked(true);
    setError(null);

    setLoading(true);
    doLogin(email, password)
      .then((response) => {
        const authUser = response.data.data;
        if (!authUser.is_verified) {
          setError("You need to verify your email before you login to the app");
          return;
        }
        if (authUser.is_active === 0) {
          setError("Your account is inactive. Please contact admin.");
          return;
        }
        toast.success("Login successful");
        clearSession();
        dispatch(fetchCustomer(authUser?.customer_id));
        dispatch(fetchLLMOptions(authUser?.user_role?.user_id));
        setUser(authUser.token, authUser?.email, authUser);
        newVersionAvailableToRefresh()
          .catch(() => {
            console.log("Check version");
          })
          .finally(() => {
            navigate("/luminaire/discoveryV2");
          });
      })
      .catch((error) => {
        console.log("Login error:", error);
        const message = error?.response?.data?.message || "Invalid Username/Password";
        toast.error(message, { autoClose: 2000 });
        setIsClicked(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <ForgotPasswordModal
        emailToResetPass={emailToResetPass}
        setEmailToResetPass={setEmailToResetPass}
        show={isShowModal}
        handleClose={handleClose}
        handleResetPassword={handleResetPassword}
        successMessage={successMessage}
      />

      <div className={classes.page}>
        <div className={classes.container}>
          <div className={classes.form_container}>
            <form onSubmit={handleSubmit} className={classes.form}>
              <h2 className={classes.login_heading}>Login</h2>
              <p className={classes.login_subtitle}>Access your Revere workspace</p>

              <TextField
                type="email"
                placeholder="Email"
                value={email}
                required
                fullWidth
                variant="outlined"
                onChange={(e) => setEmail(e.target.value)}
                className={classes.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ color: "#b0b7c3", fontSize: "20px" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    fontSize: "14.5px",
                    height: "46px",
                    overflow: "hidden",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#dde1e7",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#4fd1c5",
                  },
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 100px #fff inset",
                    WebkitTextFillColor: "#1a2332",
                  },
                }}
              />

              <TextField
                type="password"
                placeholder="Password"
                value={password}
                required
                fullWidth
                variant="outlined"
                onChange={(e) => setPassword(e.target.value)}
                className={classes.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: "#b0b7c3", fontSize: "20px" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    fontSize: "14.5px",
                    height: "46px",
                    overflow: "hidden",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#dde1e7",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#4fd1c5",
                  },
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 100px #fff inset",
                    WebkitTextFillColor: "#1a2332",
                  },
                }}
              />

              <div
                className={classes.forgot_pass}
                onClick={() => setIsShowModal(true)}
              >
                <span>Forgot password?</span>
              </div>

              {error && <ErrorMessage message={error} />}

              <MUIButton
                loading={loading}
                className={classes.login_btn}
                type="submit"
              >
                Login Now
              </MUIButton>
            </form>
          </div>

          <div
            className={classes.background_section}
            style={{ backgroundImage: `url(${loginBg})` }}
          >
            <div className={classes.brand_logo}>
              <img src={brandIcon} alt="Brand Icon" className={classes.brand_icon} />
              <span className={classes.brand_text}>Revere</span>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </>
  );
};

export default AuthComponent;
